import React, { useEffect, useState } from 'react';

const LINES = [
    'Generating your Talent Card…',
    'We are building something that changes what happens next in your career.',
    'Not a new job board. Not a better screening process. Something that doesn’t exist yet.',
    'The Founding 1000 will be the first to experience it before anyone else.',
    'We can’t tell you exactly what it is yet. But when it launches you will understand immediately why we built it this way.',
    'Your Talent Card is ready.'
];

const CHAR_DELAY_MS = 28;
const INTER_LINE_PAUSE_MS = 500;
const FINAL_LINE_INDEX = LINES.length - 1;

/**
 * Full-screen typewriter sequence shown while the candidate's Talent Card is being
 * generated. The first lines type out regardless of API state. The final line
 * ("Your Talent Card is ready.") is gated on apiComplete; once both the typing
 * AND the API call have completed, the pulsing "View your Talent Card" button
 * is revealed. No feedback collection happens on this screen — feedback has its
 * own home in the FoundingMemberFAB on the candidate dashboard.
 */
const GeneratingTalentCardScreen = ({ apiComplete, onContinue }) => {
    const [lineIdx, setLineIdx] = useState(0);
    const [charIdx, setCharIdx] = useState(0);
    const [completedLines, setCompletedLines] = useState([]);

    const isFinalLine = lineIdx === FINAL_LINE_INDEX;
    const allDone = completedLines.length === LINES.length;
    const waitingForApi = isFinalLine && !apiComplete && !allDone;

    useEffect(() => {
        if (allDone) return;
        if (waitingForApi) return;
        if (lineIdx >= LINES.length) return;

        const currentLine = LINES[lineIdx];

        if (charIdx < currentLine.length) {
            const timer = setTimeout(() => setCharIdx(c => c + 1), CHAR_DELAY_MS);
            return () => clearTimeout(timer);
        }

        // Line just finished typing — pause, then commit and advance.
        const timer = setTimeout(() => {
            setCompletedLines(prev => [...prev, currentLine]);
            setLineIdx(i => i + 1);
            setCharIdx(0);
        }, INTER_LINE_PAUSE_MS);
        return () => clearTimeout(timer);
    }, [lineIdx, charIdx, apiComplete, allDone, waitingForApi]);

    const inProgressLine = lineIdx < LINES.length ? LINES[lineIdx].slice(0, charIdx) : '';

    const lineStyle = {
        fontFamily: '"JetBrains Mono", "Fira Code", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        color: '#9333EA',
        fontSize: '1.0625rem',
        lineHeight: 1.7,
        letterSpacing: '0.01em',
        wordBreak: 'break-word'
    };

    const cursorStyle = {
        display: 'inline-block',
        width: '0.55ch',
        marginLeft: '2px',
        background: '#8B5CF6',
        color: '#8B5CF6',
        animation: 'sampler-cursor-blink 1s steps(2, start) infinite',
        userSelect: 'none'
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                backgroundColor: '#F0FDF4',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                padding: '8vh 24px'
            }}
            data-testid="talent-card-generating-screen"
        >
            <div style={{ width: '100%', maxWidth: '880px', textAlign: 'left' }}>
                {completedLines.map((line, i) => (
                    <div key={`done-${i}`} style={{ ...lineStyle, marginBottom: '1.1rem' }} data-testid={`generating-line-done-${i}`}>
                        {line}
                    </div>
                ))}

                {!allDone && !waitingForApi && lineIdx < LINES.length && (
                    <div style={{ ...lineStyle, marginBottom: '1.1rem' }} data-testid={`generating-line-active-${lineIdx}`}>
                        {inProgressLine}
                        <span style={cursorStyle} aria-hidden="true">&nbsp;</span>
                    </div>
                )}

                {waitingForApi && (
                    <div style={{ ...lineStyle, marginBottom: '1.1rem' }} data-testid="generating-line-waiting">
                        <span style={cursorStyle} aria-hidden="true">&nbsp;</span>
                        <div style={{ fontFamily: '"Barlow", sans-serif', color: '#6B7280', fontSize: '0.9rem', marginTop: '0.75rem', letterSpacing: 'normal' }}>
                            Still working on it — this can take up to a minute or two. Please don&rsquo;t close this tab.
                        </div>
                    </div>
                )}

                {allDone && (
                    <div style={{ marginTop: '2.5rem' }}>
                        <button
                            type="button"
                            onClick={onContinue}
                            data-testid="view-talent-card-btn"
                            style={{
                                fontFamily: 'Unbounded, sans-serif',
                                fontSize: '1rem',
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                color: '#FFFFFF',
                                background: '#8B5CF6',
                                border: 'none',
                                borderRadius: '999px',
                                padding: '16px 36px',
                                cursor: 'pointer',
                                animation: 'sampler-violet-pulse 2.2s ease-in-out infinite',
                                transition: 'transform 0.15s ease'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            View your Talent Card
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes sampler-cursor-blink {
                    0%, 49% { opacity: 1; }
                    50%, 100% { opacity: 0; }
                }
                @keyframes sampler-violet-pulse {
                    0%, 100% {
                        box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.55),
                                    0 8px 24px rgba(139, 92, 246, 0.35);
                    }
                    50% {
                        box-shadow: 0 0 0 16px rgba(139, 92, 246, 0),
                                    0 8px 24px rgba(139, 92, 246, 0.45);
                    }
                }
            `}</style>
        </div>
    );
};

export default GeneratingTalentCardScreen;
